import color from 'color';
import * as React from 'react';
import { useMemo } from 'react';
import {
  ActivityIndicator,
  StyleProp,
  StyleSheet,
  Text,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { useTheme } from 'styled-components';
import { useOnPromise } from '../../functions/hooks/useOnPromise';
import { useResponsiveContext } from '../../functions/responsive/ResponsiveContext';
import { FCC } from '../../utils/react';
import { Colors } from '../styles/Colors';
import { ButtonTextStyleProvider } from './ButtonFramework/Button';
import { useButtonStylePresets } from './ButtonFramework/presets';

export interface ButtonVariantProps {
  testID?: string;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  backgroundColor?: string;
  disabled?: boolean;
  onPress?: () => void;
}
export const ButtonVariant: FCC<ButtonVariantProps> = props => {
  const theme = useTheme();

  const { disabled } = props;

  const backgroundColor = disabled
    ? Colors.DisabledSolid
    : props.backgroundColor ?? theme.colors.primaryDarkColor;

  const whiteText = disabled ? true : !color(backgroundColor).isLight();

  const { normalStyle, normalTextStyle } = useButtonStylePresets();

  const textStyle = useMemo(
    () =>
      StyleSheet.flatten([
        normalTextStyle,
        { color: whiteText ? 'white' : 'black' },
        props.textStyle,
      ]),
    [normalTextStyle, props.textStyle, whiteText],
  );

  const style: StyleProp<ViewStyle> = useMemo(
    () => [normalStyle, { backgroundColor: backgroundColor }, props.style],
    [backgroundColor, normalStyle, props.style],
  );

  return (
    <ButtonTextStyleProvider style={textStyle}>
      <TouchableOpacity
        testID={props.testID}
        onPress={props.onPress}
        disabled={props.disabled}
        style={style}
      >
        {props.children}
      </TouchableOpacity>
    </ButtonTextStyleProvider>
  );
};

export type ButtonParams = {
  title: string;
  loading?: boolean;
  disabled?: boolean;
  backgroundColor?: string;
  whiteText?: boolean;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  normalTextStyle?: StyleProp<TextStyle>;
  testID?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  onPress?: () => void;
};

const Button: React.FC<ButtonParams> = props => {
  const { rpx } = useResponsiveContext();
  const theme = useTheme();
  const { disabled, loading } = props;
  const { normalTextStyle } = useButtonStylePresets();
  const backgroundColor = disabled
    ? Colors.DisabledSolid
    : props.backgroundColor ?? theme.colors.primaryDarkColor;
  const whiteText =
    props.whiteText ?? disabled ? true : !color(backgroundColor).isLight();

  // default icon position is left as it was previously
  const iconPosition = props.iconPosition ?? 'left';

  return (
    <ButtonVariant
      testID={props.testID}
      disabled={props.disabled || loading}
      backgroundColor={props.backgroundColor}
      style={props.style}
      onPress={props.onPress}
    >
      {iconPosition === 'left' && props.icon && (
        <View
          css={`
            margin-right: ${rpx(10)}px;
          `}
        >
          {props.icon}
        </View>
      )}
      {loading && (
        <ActivityIndicator
          css={`
            margin-left: ${rpx(-12)}px;
            margin-right: ${rpx(4)}px;
          `}
          color={whiteText ? 'white' : 'black'}
        />
      )}
      <Text
        numberOfLines={1}
        style={[
          props.normalTextStyle ?? normalTextStyle,
          {
            marginRight: loading ? rpx(-12) : 0,
            color: whiteText ? 'white' : 'black',
          },
          props.textStyle,
        ]}
      >
        {props.title}
      </Text>
      {iconPosition === 'right' && props.icon && (
        <View
          css={`
            margin-left: ${rpx(10)}px;
          `}
        >
          {props.icon}
        </View>
      )}
    </ButtonVariant>
  );
};

export default Button;

export const AsyncButton: React.FC<
  Omit<ButtonParams, 'onPress'> & {
    onPress?: () => Promise<void>;
  }
> = ({ onPress, ...props }) => {
  const onPromise = useOnPromise(async () => {
    await onPress?.();
  });
  return (
    <Button
      {...props}
      loading={onPromise.loading}
      onPress={onPromise.onPromise}
    />
  );
};
